<template>
    <div class="payment-form">
        <div
            v-if="isLoading"
            class="spinner md-layout md-alignment-center-center"
            :class="{
                active: isLoading,
            }"
        >
            <md-progress-spinner md-mode="indeterminate" />
        </div>

        <div v-if="setupError" class="pp-setup-error">
            <md-empty-state
                class="error-icon"
                md-icon="error"
                md-label="Error"
                md-description="The payment processor for this site has not been setup yet. Please contact the site administrator."
            >
            </md-empty-state>
        </div>

        <div
            :class="{ hidden: isLoading || setupError }"
            class="payment-details"
        >
            <div
                v-if="defaultPaymentMethod && !requireNewPaymentMethod"
                id="pm-choice"
                class="md-layout md-alignment-center-space-between"
            >
                <div id="current-pm-title">Default Payment Method</div>
                <div id="current-pm">
                    <md-icon class="md-size-2x">credit_card</md-icon>
                    <span id="current-pm-last4"
                        >&#8226;&#8226;&#8226;&#8226;{{
                            defaultPaymentMethod.last4
                        }}</span
                    >
                    <span id="current-pm-exp"
                        >Exp: {{ defaultPaymentMethod.expirationDate }}</span
                    >
                </div>

                <div v-if="!requireNewPaymentMethod">
                    <md-switch
                        v-model="useNewPaymentMethod"
                        :disabled="isLoading || !paymentOptions?.clientSecret"
                        class="md-primary"
                        >Use New Payment Method</md-switch
                    >
                </div>
            </div>
            <!-- spacer -->
            <div></div>

            <div v-if="!!paymentOptions?.clientSecret">
                <stripe-payment
                    v-if="paymentProcessor.brand === 'stripe'"
                    v-show="!selectedPaymentMethod"
                    ref="paymentRef"
                    :pk="paymentProcessor.key"
                    :elements-options="paymentOptions"
                    :confirm-params="paymentParams"
                    redirect="if_required"
                    @confirmed="onConfirmed"
                    @loading="onLoading"
                    @element-change="onElementChange"
                    @error="onError"
                    @element-ready="onReady"
                />
                <auth-dot-net-payment
                    v-else-if="paymentProcessor.brand === 'authdotnet'"
                    v-show="!selectedPaymentMethod"
                    ref="paymentRef"
                    :pk="paymentProcessor.key"
                    :elements-options="paymentOptions"
                    :confirm-params="paymentParams"
                    redirect="if_required"
                    @confirmed="onConfirmed"
                    @loading="onLoading"
                    @element-change="onElementChange"
                    @error="onError"
                    @element-ready="onReady"
                />
                <ezic-payment
                    v-else-if="paymentProcessor.brand === 'ezic'"
                    v-show="!selectedPaymentMethod"
                    ref="paymentRef"
                    :pk="paymentProcessor.key"
                    :elements-options="paymentOptions"
                    :confirm-params="paymentParams"
                    redirect="if_required"
                    @confirmed="onConfirmed"
                    @loading="onLoading"
                    @element-change="onElementChange"
                    @error="onError"
                    @element-ready="onReady"
                />
            </div>
        </div>
        <!-- eslint-disable-next-line -->
        <div id="payment-element-errors" role="alert" v-html="errorMessage"></div>
    </div>
</template>

<script>
const StripePayment = () =>
    import(
        /* webpackChunkName: "stripe-payment-element" */ '@/components/StripePayment.vue'
    );
const AuthDotNetPayment = () =>
    import(
        /* webpackChunkName: "authdotnet-payment-element" */ '@/components/AuthDotNetPayment.vue'
    );
const EzicPayment = () =>
    import(
        /* webpackChunkName: "ezic-payment-element" */ '@/components/EzicPayment.vue'
    );
import _find from 'lodash/find';
import _debounce from 'lodash/debounce';

export default {
    name: 'PaymentForm',
    components: {
        StripePayment,
        AuthDotNetPayment,
        EzicPayment,
    },
    props: {
        product: {
            type: Object,
            default: undefined,
        },
        purchaseDetails: {
            type: Object,
            default: undefined,
        },
        forceNewPaymentMethod: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: 'payment',
        },
        captchaAction: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        loadingCount: 0,
        pendingUpdate: undefined,
        updateResolver: undefined,
        debounceCancel: undefined,
        ready: false,
        elementComplete: false,
        awaitingStatus: false,
        authed: false,
        errorMessage: undefined,
        setupError: false,
        paymentProcessor: { brand: undefined, key: '' },
        paymentOptions: {},
        paymentId: undefined,
        userInfo: undefined,
        useNewPaymentMethod: true,
        userPaymentMethods: [],
        defaultPaymentMethod: undefined,
        cartId: undefined,
        affiliateMarketingPublicKey: undefined,
        affiliateMarketingPlatform: undefined,
    }),
    computed: {
        paymentParams: function () {
            const params = {
                return_url: window.location.href,
            };
            if (this.selectedPaymentMethod?.gatewayId) {
                params.payment_method = this.selectedPaymentMethod?.gatewayId;
            }
            return params;
        },
        selectedPaymentMethod: function () {
            if (!this.useNewPaymentMethod && this.ready) {
                this.$emit('complete', true);
            } else if (this.ready) {
                this.$refs?.paymentRef?.clear();
                this.$emit('complete', false);
            }
            return this.useNewPaymentMethod || this.requireNewPaymentMethod
                ? undefined
                : this.defaultPaymentMethod;
        },
        requireNewPaymentMethod: function () {
            return (
                !this.userPaymentMethods?.length || this.forceNewPaymentMethod
            );
        },
        isLoading: function () {
            this.$emit('loading', !!this.loadingCount);
            return !!this.loadingCount;
        },
        emitComplete: function () {
            if (this.ready && this.selectedPaymentMethod) {
                this.$emit('complete', true);
            }
            return this.ready;
        },
    },
    watch: {
        product: {
            deep: true,
            handler: function () {
                this.generateIntent();
            },
        },
        purchaseDetails: {
            deep: true,
            handler: function () {
                //this promise allows waiting for the update before submitting
                //the payment confirmation
                this.pendingUpdate =
                    this.pendingUpdate ||
                    new Promise((resolve, reject) => {
                        this.updateResolver = {
                            resolve: (val) => {
                                this.updateResolver = undefined;
                                resolve(val);
                            },
                            reject: (e) => {
                                this.updateResolver = undefined;
                                reject(e);
                            },
                        };
                    });

                if (typeof this.debounceCancel?.cancel === 'function') {
                    this.debounceCancel.cancel();
                }
                this.debounceCancel = this.debounceIntentUpdate();
            },
        },
    },
    async created() {
        const settings = await this.$appSettings;
        this.affiliateMarketingPlatform = settings.affiliateMarketingPlatform;
        this.affiliateMarketingPublicKey = settings.affiliateMarketingPublicKey;
        this.paymentProcessor = settings.paymentProcessor;
        this.authed = this.$gideo.authed;
        if (!this.paymentProcessor?.brand) {
            this.setupError = true;
            this.$emit('error', {
                error: 'Missing Payment Processor',
            });
        }

        const setAuthed = () => {
            if (this.authed !== this.$gideo.authed) {
                this.authed = this.$gideo.authed;
                this.loadUserPaymentMethods();
            }
        };
        this.$gideo.bus.$on('logged-in', setAuthed);
        this.$gideo.bus.$on('logged-out', setAuthed);
        await this.loadUserPaymentMethods();
        await this.generateIntent();
    },
    methods: {
        async generateIntent() {
            if (!this.product && this.type === 'payment') {
                return;
            }

            this.errorMessage = undefined;
            this.loadingCount++;
            this.$emit('complete', false);

            let captcha = undefined;
            if (this.paymentProcessor?.requiresManualFraudPrevention) {
                await this.$recaptchaLoaded();
                captcha = await this.$recaptcha(this.captchaAction);
            }
            try {
                const pi =
                    this.type === 'payment'
                        ? await this.$gideo.createPaymentIntent(
                              this.product.productId,
                              this.selectedPaymentMethod?.id,
                              captcha,
                              this.purchaseDetails,
                          )
                        : await this.$gideo.createSetupIntent(captcha);
                this.paymentOptions.clientSecret = pi.secret;
                this.paymentId = pi.id;
                this.cartId = pi.cartId;
            } catch (e) {
                this.errorMessage = this.sanitizeErrorMessage(e.userMessage);
                e.userMessage = this.errorMessage;
                this.$emit('error', e);
            }
            this.loadingCount--;
            if (this.selectedPaymentMethod || this.elementComplete) {
                this.$emit('complete', true);
            }
        },
        async updateIntent() {
            if (!this.paymentId) {
                return;
            }

            this.errorMessage = undefined;
            this.$emit('complete', false);

            let captcha = undefined;
            if (this.paymentProcessor?.requiresManualFraudPrevention) {
                await this.$recaptchaLoaded();
                captcha = await this.$recaptcha(this.captchaAction);
            }
            try {
                const pi =
                    this.type === 'payment'
                        ? await this.$gideo.updatePaymentIntent(
                              this.paymentId,
                              this.product.productId,
                              this.selectedPaymentMethod?.id,
                              captcha,
                              this.purchaseDetails,
                          )
                        : await this.$gideo.updateSetupIntent(
                              this.paymentId,
                              captcha,
                          );
                this.paymentOptions.clientSecret = pi.secret;
                this.paymentId = pi.id;
                this.cartId = pi.cartId;
            } catch (e) {
                this.errorMessage = this.sanitizeErrorMessage(e.userMessage);
                e.userMessage = this.errorMessage;
                this.$emit('error', e);
            }
            if (this.selectedPaymentMethod || this.elementComplete) {
                this.$emit('complete', true);
            }
        },
        sanitizeErrorMessage(errorMessage) {
            return errorMessage
                ?.replace(/<\s*img(.*?)<\/\s*img\s*>/gm, '')
                ?.replace(/<\s*script(.*?)<\/\s*script\s*>/gm, '');
        },
        debounceIntentUpdate: _debounce(async function () {
            const res = !this.paymentId
                ? await this.generateIntent()
                : await this.updateIntent();
            this.updateResolver?.resolve(res);
            this.pendingUpdate = undefined;
        }, 1000),
        async loadUserPaymentMethods() {
            this.loadingCount++;
            this.$emit('complete', false);
            if (!this.authed) {
                this.useNewPaymentMethod = true;
                this.userInfo = undefined;
                this.userPaymentMethods = [];
                this.loadingCount--;
                return;
            }
            const userInfo = await this.$gideo.getUserInfo();
            this.userInfo = userInfo;
            this.userPaymentMethods = userInfo.paymentMethods;
            this.defaultPaymentMethod = _find(userInfo.paymentMethods, {
                default: true,
            });
            if (
                !this.defaultPaymentMethod?.last4 ||
                !this.defaultPaymentMethod?.expirationDate
            ) {
                this.defaultPaymentMethod = undefined;
            }
            this.useNewPaymentMethod = !this.defaultPaymentMethod;
            this.loadingCount--;
            if (this.selectedPaymentMethod || this.elementComplete) {
                this.$emit('complete', true);
            }
        },
        onElementChange(event) {
            if (event.complete) {
                this.$emit('complete', true);
                this.elementComplete = true;
            } else if (!this.selectedPaymentMethod) {
                this.$emit('complete', false);
                this.elementComplete = false;
            }
        },
        onReady() {
            this.ready = true;
        },
        onLoading(isLoading) {
            if (isLoading || !this.awaitingStatus) {
                this.$emit('confirming', isLoading);
            }
        },
        onError(error) {
            if (error.code === 'already_confirmed') {
                this.onConfirmed();
            } else {
                error.userMessage = this.sanitizeErrorMessage(
                    error.userMessage || error.message,
                );
                this.errorMessage = error.userMessage;
                this.$emit('error', error);
            }
        },
        async onConfirmed() {
            this.awaitingStatus = true;
            this.errorMessage = undefined;
            try {
                if (this.type === 'payment') {
                    await this.$gideo.awaitPaymentConfirmation(
                        this.paymentId,
                        this.product.type === 'donation',
                        this.product,
                    );
                    if (
                        this.affiliateMarketingPlatform === 'refersion' &&
                        this.product.type !== 'donation'
                    ) {
                        //send order tracking event to refersion
                        const rfsn = {
                            cart: this.cartId,
                            id: localStorage.getItem('rfsn_v4_id'),
                            url: window.location.href,
                            aid: localStorage.getItem('rfsn_v4_aid'),
                            cs: localStorage.getItem('rfsn_v4_cs'),
                        };
                        if (typeof global.r?.sendCheckoutEvent === 'function') {
                            global.r.sendCheckoutEvent(
                                rfsn.cart,
                                rfsn.id,
                                rfsn.url,
                                rfsn.aid,
                                rfsn.cs,
                            );
                        } else {
                            //eslint-disable-next-line
                            console.warn(
                                'Refersion tracking system object not found',
                            );
                        }
                    }
                } else {
                    await this.$gideo.awaitSetupConfirmation(this.paymentId);
                }
                this.$emit('confirmed');
            } catch (e) {
                this.errorMessage = this.sanitizeErrorMessage(e.userMessage);
                e.userMessage = this.errorMessage;
                this.$emit('error', e);
            } finally {
                this.awaitingStatus = false;
                this.$emit('confirming', false);
            }
        },
        async submit() {
            await this.pendingUpdate;
            return this.$refs?.paymentRef?.submit();
        },
        focus() {
            this.$refs?.paymentRef?.focus();
        },
        reset() {
            this.paymentId = undefined;
            this.errorMessage = undefined;
            this.$refs?.paymentRef?.clear();
            this.$emit('complete', false);
        },
    },
};
</script>

<style scoped>
#pm-choice {
    position: relative;
    padding: 1.5em 0 1em 0;
}
#current-pm-title {
    position: absolute;
    top: 0.2em;
    left: 0;
}
#current-pm-last4,
#current-pm-exp {
    padding: 0.5em;
}
.payment-form {
    position: relative;
}
.spinner {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
}
.payment-details {
    min-height: 6rem;
}
.hidden {
    opacity: 0;
    z-index: -1;
}
#payment-element-errors {
    text-align: center;
    padding: 1em;
    color: #ff1744;
    line-height: 1.1em;
    min-height: 3.1em;
    font-size: 12px;
    transition: 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
</style>
